import React, { Reducer, useCallback, useEffect, useMemo, useReducer } from 'react';
import getConfig from 'next/config';
import Pusher from 'pusher-js';
import dayjs from 'dayjs';
import ParagraphProps from '@src/components/Paragraph/paragraph-props';
import { LiveblogPost, Paragraph } from '@src/interfaces';
import fetchLiveblogPosts, { LiveblogPostsResponse } from '@src/utils/api/fetch-liveblog-posts';
import { trackEvent, trackPage } from '@src/utils/et';
import dynamic from 'next/dynamic';
import LiveblogAd from "@src/components/Ad/LiveblogAd";
import {Models} from "@data/et-web-api";
import CleverpushTopicButton from '../Cleverpush/TopicButton';

interface LiveblogDeleteMessage {
  id: string;
}

interface LiveblogParagraphProps {
  id: string;
  title: string;
  html: string;
  live: boolean;
  liveTag?: string;
  initial: number;
  limit: number;
  cleverpushTopic?: string;
  disable_ad_5: boolean;
}

interface LiveblogParagraphState {
  posts: LiveblogPost[];
  hasMore: boolean;
  offset: number;
  loading: boolean;
}

export type Action =
  | { type: 'loading' }
  | { type: 'append'; response: LiveblogPostsResponse }
  | { type: 'prepend'; newPost: LiveblogPost }
  | { type: 'edit'; newPost: LiveblogPost }
  | { type: 'delete'; message: LiveblogDeleteMessage };

const appendResponse = (state: LiveblogParagraphState, response: LiveblogPostsResponse) => {
  const newPosts = state.posts.concat(response.posts);
  return {
    posts: newPosts,
    hasMore: newPosts.length < response.total,
    offset: newPosts.length,
    loading: false,
  };
};

const prependPost = (state: LiveblogParagraphState, newPost: LiveblogPost) => ({
  ...state,
  posts: [newPost, ...state.posts],
});

const editPost = (state: LiveblogParagraphState, newPost: LiveblogPost) => {
  const newPosts = state.posts;
  const oldPost = newPosts.find(post => post.id === newPost.id);
  if (oldPost) {
    const oldPostIndex = newPosts.indexOf(oldPost);
    newPosts[oldPostIndex] = newPost;
  }
  return {
    ...state,
    posts: newPosts,
  };
};

const deletePost = (state: LiveblogParagraphState, { id }: LiveblogDeleteMessage) => {
  const newPosts = state.posts.filter(post => post.id !== id);
  return {
    ...state,
    posts: newPosts,
  };
};

const reducer: Reducer<LiveblogParagraphState, Action> = (state, action) => {
  switch (action.type) {
    case 'loading':
      return {
        ...{ ...state },
        loading: true,
      };

    case 'append':
      return appendResponse(state, action.response);

    case 'prepend':
      return prependPost(state, action.newPost);

    case 'edit':
      return editPost(state, action.newPost);

    case 'delete':
      return deletePost(state, action.message);

    default:
      console.warn('Liveblog unhandled action', action);
      return state;
  }
};

const ParagraphComponent = dynamic(
  () =>
    // eslint-disable-next-line import/no-cycle
    import('@src/components/Paragraph/ParagraphComponent'),
);

const LiveblogParagraph = ({ paragraph }: ParagraphProps) => {
  const model = paragraph.data as LiveblogParagraphProps;
  const initialState: LiveblogParagraphState = useMemo(
    () => ({
      posts: [],
      hasMore: true,
      offset: 0,
      limit: model.initial,
      loading: false,
    }),
    [model.initial],
  );
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadMore = useCallback(
    (offset: number, limit: number) => {
      dispatch({ type: 'loading' });
      fetchLiveblogPosts(model.id, offset, limit).then((response: LiveblogPostsResponse) => {
        if (response.total > 0) {
          dispatch({ type: 'append', response });
        }
      });
    },
    [model.id],
  );

  useEffect(() => {
    loadMore(0, model.initial);
  }, [loadMore, model.initial]);

  useEffect(() => {
    const { publicRuntimeConfig } = getConfig();
    const pusher = new Pusher(publicRuntimeConfig.PUSHER_KEY, {
      cluster: 'eu',
    });
    const channel = pusher.subscribe(`liveblog-headless-${model.id}`);
    channel.bind('add', (newPost: LiveblogPost) => {
      dispatch({ type: 'prepend', newPost });
    });
    channel.bind('edit', (newPost: LiveblogPost) => {
      dispatch({ type: 'edit', newPost });
    });
    channel.bind('delete', (message: LiveblogDeleteMessage) => {
      dispatch({ type: 'delete', message });
    });

    return () => {
      channel.unsubscribe();
    };
  }, [model.id]);

  const onClickMore = useCallback(() => {
    loadMore(state.offset, model.limit);
    trackPage({});

    const eventName: Models.ShowMoreClick['eventName'] = 'Show More Click';
    const eventPayload: Models.ShowMoreClick['eventPayload'] = {
      laneName: 'Liveblog',
      numberOfTeasers: state.offset,
      laneTrackingId: model.title,
    }
    trackEvent(eventName, eventPayload);
  }, [loadMore, model.limit, model.title, state.offset]);

  const postElems = state.posts.map((post: LiveblogPost, pos) => {
    const paragraphs = post.paragraphs.map((postParagraph: Paragraph, index) => (
      <ParagraphComponent key={index} paragraph={postParagraph} />
    ));
    return (
      <div key={post.id} className="liveblog-post">
        <span className="breadcrumb">
          {dayjs.unix(post.timestamp).format('HH:mm · DD.MM.YYYY')}
        </span>

        {post.title ? <h3>{post.title}</h3> : null}

        <div
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{ __html: post.html }}
        />

        <div>{paragraphs}</div>

        {(model.disable_ad_5 !== true) && (
          <>
            {(pos === 0) && (<LiveblogAd id={post.id} slot="performanceRectangle1" />)}
            {(pos === 2) && (<LiveblogAd id={post.id} slot="performanceRectangle2" />)}
            {(pos === 4) && (<LiveblogAd id={post.id} slot="performanceRectangle3" />)}
            {(pos === 6) && (<LiveblogAd id={post.id} slot="performanceRectangle4" />)}
            {(pos === 8) && (<LiveblogAd id={post.id} slot="performanceRectangle5" />)}
          </>
        )}
      </div>
    );
  });

  return (
    <section className="paragraph liveblog-paragraph">
      <span className="live-tag">
        <span className="text-highlight">{model.liveTag}</span>

        {model.cleverpushTopic ? <CleverpushTopicButton id={model.cleverpushTopic} name="Benachrichtigungen" /> : null}
      </span>

      {model.title ? <h2>{model.title}</h2> : null}

      {model.html ? <div
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{ __html: model.html }}
        /> : null}

      <div className="liveblog-posts">{postElems}</div>

      {state.hasMore ? <div className={`load-more${state.loading ? ' is-loading' : ''}`}>
        <button
          aria-label="Mehr Inhalte"
          className="btn btn-red"
          onClick={onClickMore}
          type="button"
          >
          Mehr Inhalte laden
          <div className="lds-dual-ring" />
        </button>
      </div> : null}
    </section>
  );
};

export default LiveblogParagraph;
